<template>
  <div>
    <draggable
      class="list-group"
      tag="ul"
      v-model="slideActive.SlideOptions"
      v-bind="dragOptions"
      @start="drag = true"
      @end="drag = false"
      @change="handleSortSlideOptions"
      v-if="slideActive.SlideOptions.length"
      :disabled="isSortingSlideOptions"
      draggable=".draggable-item"
    >
      <CorrectOrderSlideOption
        v-for="(slideOption, index) in slideActive.SlideOptions"
        :key="slideOption.order + ' '+ index"
        :index="index"
        :slideOption="slideOption"
        :is-deletable="slideOptions.length > 2"
        @handleDeleteSlideOption="handleDeleteSlideOption"
        :handleSlideOptionChanged="handleEditTitleSlideOption"
        @handleAddNewSlideOption="handleAddNewOption"
        :is-dragging-disabled="isSortingSlideOptions || disableSortSlideOptions || !slideOption.id"
      />
    </draggable>

    <AhaButton
      class="w-full"
      icon-name="add"
      @click="handleAddNewOption"
      v-if="slideOptions.length < 7"
    >
      Add Statement
    </AhaButton>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import Socket from '@/socket'
import EventBus from '@/eventBus'

// api
import SlideOptionAPI from '@/api/slideOption'

// components
import CorrectOrderSlideOption from '@/components-v2/presentation-editor/side-right/CorrectOrderSlideOption.vue'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import Config from '@/config'

const correctOrderChanges = 'correct-order-changes'
const redrawMasonry = 'redraw-masonry-correct-order'
const updateCanvasContent = 'update-correct-order-canvas-content'

const slideOptionHasId = (slideOption) => {
  return slideOption.id
}

export default {
  components: {
    draggable,
    CorrectOrderSlideOption,
  },
  data() {
    return {
      key: '12345',
      drag: false,
      isSortingSlideOptions: false,
      disableSortSlideOptions: false,
      arrPromiseUpdateTitleOption: [],
      mappingUpdateOptionData: {},
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      slideOptions: (state) => state.slideActive.SlideOptions,
    }),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        delay: 0,
        ghostClass: 'ghost',
        handle: '.handle-drag',
      }
    },
    isDefaultContent() {
      return this.slideOptions.every((option) => !option.id)
    },
  },
  mixins: [UndoRedoMixin],
  async mounted() {
    this.registerHistory()
    if (this.slideOptions.some(slideOptionHasId)) {
      const newSlideOptions = this.slideOptions
        .filter(slideOptionHasId)
        .sort((a, b) => a.order - b.order)
      this.updateSlideActiveAction({ SlideOptions: newSlideOptions })
      EventBus.$emit(correctOrderChanges)
    }

    if (this.slideOptions.length === 0) {
      const newSlideOptions = [
        {
          title: null,
          order: 1,
        },
        {
          title: null,
          order: 2,
        },
        {
          title: null,
          order: 3,
        },
        {
          title: null,
          order: 4,
        },
        {
          title: null,
          order: 5,
        },
      ]

      this.updateSlideActiveAction({ SlideOptions: newSlideOptions })
      EventBus.$emit(correctOrderChanges)
    }
    EventBus.$on('add-new-slide-option', () => {
      this.handleAddNewOption()
    })
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    ...mapGetters(['getSlideOptionsActive']),

    backupAddNewOption() {
      // const newSlideOptions = JSON.parse(JSON.stringify(slideOptions))
      //
      // const maxOrder = slideOptions.reduce((maxNumber, slideOption) => {
      //   return slideOption.order > maxNumber ? slideOption.order : maxNumber
      // }, 0)
      //
      // newSlideOptions.push({
      //   index: slideOptions.length + 1,
      //   order: maxOrder + 1,
      //   color: utils.getAhaColor(slideOptions.length),
      // })
      setTimeout(() => {
        this.addActionToHistory({
          type: 'AddNewOption',
          data: {},
          newData: {},
          slideActiveId: this.slideActive.id,
        })
      }, 300)
    },

    async undoAddNewOption() {
      const currentSideOptions = JSON.parse(JSON.stringify(this.getSlideOptionsActive()))
      await this.handleDeleteSlideOption(currentSideOptions[currentSideOptions.length - 1], currentSideOptions.length - 1, false)
      // await this.updateSlideActiveAction({ SlideOptions: JSON.parse(JSON.stringify(this.getSlideOptionsActive())) })
      this.key = (new Date()).getTime()
      EventBus.$emit(correctOrderChanges)
    },

    async redoAddNewOption() {
      await this.handleAddNewOption(false)
      this.key = (new Date()).getTime()
      EventBus.$emit(correctOrderChanges)
    },

    async handleAddNewOption(isBackup = true) {
      if (this.slideOptions.length >= 7) return

      if (isBackup) {
        this.backupAddNewOption(this.slideOptions)
      }

      const maxOrder = this.slideOptions.reduce((maxNumber, slideOption) => {
        return slideOption.order > maxNumber ? slideOption.order : maxNumber
      }, 0)

      const currentSlideOptions = JSON.parse(JSON.stringify(this.getSlideOptionsActive()))
      currentSlideOptions.push({
        index: currentSlideOptions.length + 1,
        order: maxOrder + 1,
      })
      this.updateSlideActiveAction({
        SlideOptions: JSON.parse(JSON.stringify(currentSlideOptions)),
      })

      if (isBackup) {
        this.focusNextInput()
      }
    },

    focusNextInput() {
      setTimeout(() => {
        const nextInput = document.querySelector(`#correct-order-input-${this.slideOptions.length - 1} input`)
        if (nextInput) {
          nextInput.focus()
        }
      }, 100)
    },

    backupDeleteSlideOption(dataHistory) {
      const SlideOptions = this.getSlideOptionsActive()
      const dataUndo = {
        item: dataHistory.item,
        index: dataHistory.index,
        slideOptions: SlideOptions,
      }
      setTimeout(() => {
        this.addActionToHistory(JSON.parse(JSON.stringify({
          type: 'DeleteSlideOption',
          data: dataUndo,
          newData: dataHistory,
          slideActiveId: this.slideActive.id,
        })))
      }, 300)
    },

    async undoDeleteSlideOption({ item, slideOptions }) {
      await this.callAPIUpdateFieldDeletedSlideOption(item.id, false)

      this.updateSlideActiveAction({
        SlideOptions: slideOptions,
      })
      this.key = (new Date()).getTime()
      EventBus.$emit(correctOrderChanges)
    },

    async callAPIUpdateFieldDeletedSlideOption(slideOptionId, isDelete) {
      await this.$http
        .put(Config.api.updateSlideOption + slideOptionId, {
          deleted: isDelete,
          presentationId: this.$store.state.presentation.id,
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.$store.getters.isPresenting,
        })
    },

    async redoDeleteSlideOption({ item, index }) {
      await this.handleDeleteSlideOption(item, index, false)
      this.key = (new Date()).getTime()
      EventBus.$emit(correctOrderChanges)
    },

    async handleDeleteSlideOption(slideOption, index, isBackup = true) {
      await Promise.all(this.arrPromiseUpdateTitleOption)
      if (isBackup) {
        this.backupDeleteSlideOption({
          item: slideOption,
          index,
        })
      }
      const currentSlideOptions = JSON.parse(JSON.stringify(this.getSlideOptionsActive()))
      if (!slideOption.id) {
        currentSlideOptions.splice(index, 1)
        this.updateSlideActiveAction({
          SlideOptions: JSON.parse(JSON.stringify(currentSlideOptions)),
        })
        EventBus.$emit(updateCanvasContent)
        return
      }
      const newSlideOptions = currentSlideOptions
        .filter((item) => item.id !== slideOption.id)

      this.updateSlideActiveAction({
        SlideOptions: JSON.parse(JSON.stringify(newSlideOptions)),
      })
      this.key = (new Date()).getTime()
      EventBus.$emit(correctOrderChanges)

      try {
        this.setSyncLoadingAction(1)
        await SlideOptionAPI.deleteSlideOption(slideOption.id, {
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
        })
        this.setSyncLoadingAction(2)
      } catch {
        this.setSyncLoadingAction(3)
      }
    },

    backupEditTitleSlideOption(slideOption, payload, key) {
      const payloadOldData = {
        ...payload,
        title: slideOption.title === undefined ? '' : slideOption.title,
      }
      const oldSlideOption = {
        ...slideOption,
        title: slideOption.title === undefined ? '' : slideOption.title,
      }
      const oldData = {
        slideOption: oldSlideOption,
        payload: payloadOldData,
      }

      const newSlideOption = {
        ...slideOption,
        title: slideOption.title === undefined ? '' : slideOption.title,
      }
      const newData = {
        slideOption: newSlideOption,
        payload,
      }
      this.addActionToHistory({
        type: 'EditTitleSlideOption',
        data: JSON.parse(JSON.stringify(oldData)),
        newData: JSON.parse(JSON.stringify(newData)),
        slideActiveId: this.slideActive.id,
      }, key)
    },

    async undoEditTitleSlideOption(data) {
      await this.handleEditTitleSlideOption(data.slideOption, data.payload, null, false)
      this.key = (new Date()).getTime()
      EventBus.$emit('update-title-option')
    },

    async redoEditTitleSlideOption(data) {
      const currentSlideOptions = this.getSlideOptionsActive()
      const { length } = currentSlideOptions
      if (data.slideOption.id && data.slideOption.index === length) {
        if (!currentSlideOptions[length - 1].id) {
          currentSlideOptions[length - 1].id = data.slideOption.id
          await this.updateSlideActiveAction({ SlideOptions: currentSlideOptions })
        }

        await this.callAPIUpdateFieldDeletedSlideOption(data.slideOption.id, false)
      }
      await this.handleEditTitleSlideOption(data.slideOption, data.payload, null, false)
      this.key = (new Date()).getTime()
      EventBus.$emit('update-title-option')
    },

    async updateTitleSlideOptionNoId(index, optionId) {
      // Đợi sau khi tạo mới slideOption, sẽ quay lại update các payload khi chưa có id slide option
      const updateOptionData = this.mappingUpdateOptionData[index]
      if (!updateOptionData) {
        return
      }
      updateOptionData.slideOption.id = optionId
      await this.handleEditTitleSlideOption(
        JSON.parse(JSON.stringify(updateOptionData.slideOption)),
        JSON.parse(JSON.stringify(updateOptionData.payload)),
        updateOptionData.key,
        false,
        true,
      )
      this.mappingUpdateOptionData[index] = null
      this.backupEditTitleSlideOption(JSON.parse(JSON.stringify(updateOptionData.slideOption)), JSON.parse(JSON.stringify(updateOptionData.payload)), updateOptionData.key)
    },

    async handleEditTitleSlideOption(slideOption, payload, key, isBackup = true, nowait = false) {
      // Bug mạng chậm khi update rồi xóa data sẽ bị đảo chật tự => sẽ phải đợi sau khi update xong rồi mới xóa
      // eslint-disable-next-line no-async-promise-executor
      const promiseUpdateTitle = new Promise(async (resolve) => {
        const slideOptionBackup = JSON.parse(JSON.stringify(slideOption))
        try {
          this.setSyncLoadingAction(1)
          this.disableSortSlideOptions = true

          let slideOptionId = slideOption.id
          if (!slideOption.id) {
            if (slideOption.isCreating) {
              this.mappingUpdateOptionData[slideOption.index] = {
                slideOption,
                payload,
                key,
              }
              this.unlockHistory(key, true)
              resolve()
              return
            }
            if (!slideOption.isCreating) {
              const newSlideOption = await this.handleCreateNewSlideOption(slideOption, payload)
              slideOptionId = newSlideOption.id
              await this.updateTitleSlideOptionNoId(slideOption.index, slideOptionId)
            }
          } else {
            await this._patchSlideOptionById(JSON.parse(JSON.stringify(slideOption)), payload)
          }
          if (isBackup) {
            slideOptionBackup.id = slideOptionId
            this.backupEditTitleSlideOption(slideOptionBackup, payload, key)
            this.unlockHistory(key, true)
          }
        } catch {
          this.setSyncLoadingAction(3)
          if (isBackup) {
            this.unlockHistory(key, false)
          }
        }
        this.disableSortSlideOptions = false
        resolve()
      })
      if (nowait) {
        return promiseUpdateTitle
      }
      this.arrPromiseUpdateTitleOption.push(promiseUpdateTitle)
      await Promise.all(this.arrPromiseUpdateTitleOption)
    },

    async handleCreateNewSlideOption(slideOption, payload) {
      const newSlideOption = this.slideOptions[slideOption.index - 1]
      if (newSlideOption) {
        newSlideOption.title = payload.title
        newSlideOption.isCreating = true
        this.updateSlideActiveAction({
          SlideOptions: this.slideOptions,
        })
      }

      // eslint-disable-next-line no-return-await
      return await this._createSlideOption({
        title: payload.title,
        order: slideOption.order,
      })
    },

    async _createSlideOption(slideOption) {
      try {
        const newSlideOption = await SlideOptionAPI.createSlideOption({
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          ...slideOption,
        })

        const newSlideOptions = this.slideOptions.map((item) => {
          return item.order === slideOption.order ? newSlideOption : item
        })

        this.updateSlideActiveAction({
          SlideOptions: newSlideOptions,
        })
        EventBus.$emit(redrawMasonry)
        EventBus.$emit(correctOrderChanges)
        this.setSyncLoadingAction(2)
        return newSlideOption
      } catch (error) {
        this.setSyncLoadingAction(3)
        return null
      }
    },

    async _patchSlideOptionById(slideOption, payload) {
      try {
        const newSlideOption = await SlideOptionAPI.patchSlideOptionById(slideOption.id, {
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          ...payload,
        })

        const newSlideOptions = this.getSlideOptionsActive().map((item) => {
          if (item.id === slideOption.id) {
            return { ...item, ...newSlideOption }
          }
          return item
        })
        this.updateSlideActiveAction({ SlideOptions: newSlideOptions })
        EventBus.$emit(correctOrderChanges, 'editTitle')
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

    _getOptionsWithId() {
      return this.slideOptions.filter((option) => option.id)
    },

    backupSortOption(newEvent) {
      const eventBackup = {
        moved: {
          element: { id: newEvent.moved.element.id },
          newIndex: newEvent.moved.oldIndex,
          oldIndex: newEvent.moved.newIndex,
        },
      }
      this.addActionToHistory({
        type: 'SortOption',
        data: eventBackup,
        newData: newEvent,
        slideActiveId: this.slideActive.id,
      })
    },

    async restoreSortOption(data) {
      const { newIndex, oldIndex } = data.moved
      await this.swapSlideOptions(newIndex, oldIndex)
      await this.handleSortSlideOptions(data, false)
    },

    async swapSlideOptions(newIndex, oldIndex) {
      const slideOptions = JSON.parse(JSON.stringify(this.getSlideOptionsActive()))

      const temp = slideOptions[oldIndex]
      slideOptions.splice(oldIndex, 1)
      slideOptions.splice(newIndex, 0, temp)

      await this.updateSlideActiveAction({
        SlideOptions: JSON.parse(JSON.stringify(slideOptions)),
      })
    },

    async handleSortSlideOptions(event, isBackup = true) {
      if (isBackup) {
        this.backupSortOption(event)
      }
      const currentSlideOptions = this.getSlideOptionsActive()
      if (currentSlideOptions.length < 2 || !event || !event.moved) return

      const { newIndex, oldIndex, element: movedSlideOption } = event.moved

      if (newIndex === oldIndex) return
      if (this.isSortingSlideOptions) return

      this.isSortingSlideOptions = true
      let newOrder = null
      if (newIndex === currentSlideOptions.length - 1) {
        newOrder = currentSlideOptions[currentSlideOptions.length - 2].order + 1
      } else if (newIndex === 0) {
        newOrder = currentSlideOptions[1].order / 2
      } else {
        newOrder =
          (currentSlideOptions[newIndex + 1].order + currentSlideOptions[newIndex - 1].order) / 2
      }

      const newSlideOptions = currentSlideOptions.map((option) => {
        if (option.id === movedSlideOption.id) {
          return { ...option, order: newOrder }
        }

        return option
      })

      this.updateSlideActiveAction({
        SlideOptions: newSlideOptions,
      })
      EventBus.$emit(correctOrderChanges, 'rearrangeOptions')

      if (!movedSlideOption.id) {
        this.isSortingSlideOptions = false
      }
      if (movedSlideOption.id) {
        this.setSyncLoadingAction(1)
        try {
          await SlideOptionAPI.patchSlideOptionById(movedSlideOption.id, {
            order: newOrder,
            presentationId: this.presentation.id,
            uniqueAccessCode: this.presentation.uniqueAccessCode,
            socketId: Socket.getSocketId(),
          })

          this.isSortingSlideOptions = false
          this.setSyncLoadingAction(2)
        } catch (error) {
          this.isSortingSlideOptions = false
          this.setSyncLoadingAction(3)
          throw error
        }
      }
    },
  },
}
</script>
